"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PythStakingClient = void 0;
const crypto = __importStar(require("crypto"));
const anchor_1 = require("@coral-xyz/anchor");
const spl_governance_1 = require("@solana/spl-governance");
const spl_token_1 = require("@solana/spl-token");
const web3_js_1 = require("@solana/web3.js");
const constants_1 = require("./constants");
const pdas_1 = require("./pdas");
const bigint_1 = require("./utils/bigint");
const bn_1 = require("./utils/bn");
const clock_1 = require("./utils/clock");
const pool_1 = require("./utils/pool");
const position_1 = require("./utils/position");
const transaction_1 = require("./utils/transaction");
const vesting_1 = require("./utils/vesting");
const wallet_1 = require("./utils/wallet");
const IntegrityPoolIdl = __importStar(require("../idl/integrity-pool.json"));
const PublisherCapsIdl = __importStar(require("../idl/publisher-caps.json"));
const StakingIdl = __importStar(require("../idl/staking.json"));
class PythStakingClient {
    connection;
    wallet;
    provider;
    stakingProgram;
    integrityPoolProgram;
    publisherCapsProgram;
    constructor(config) {
        const { connection, wallet = wallet_1.DummyWallet } = config;
        this.connection = connection;
        this.wallet = wallet;
        this.provider = new anchor_1.AnchorProvider(this.connection, this.wallet, {
            skipPreflight: true,
        });
        this.stakingProgram = new anchor_1.Program(StakingIdl, this.provider);
        this.integrityPoolProgram = new anchor_1.Program(IntegrityPoolIdl, this.provider);
        this.publisherCapsProgram = new anchor_1.Program(PublisherCapsIdl, this.provider);
    }
    async initGlobalConfig(config) {
        const globalConfigAnchor = (0, bn_1.convertBigIntToBN)(config);
        const instruction = await this.stakingProgram.methods
            .initConfig(globalConfigAnchor)
            .instruction();
        return (0, transaction_1.sendTransaction)([instruction], this.connection, this.wallet);
    }
    async getGlobalConfig() {
        const globalConfigAnchor = await this.stakingProgram.account.globalConfig.fetch((0, pdas_1.getConfigAddress)()[0]);
        return (0, bn_1.convertBNToBigInt)(globalConfigAnchor);
    }
    /** Gets a users stake accounts */
    async getAllStakeAccountPositions(owner) {
        const positionDataMemcmp = this.stakingProgram.coder.accounts.memcmp("positionData");
        const res = await this.stakingProgram.provider.connection.getProgramAccounts(this.stakingProgram.programId, {
            encoding: "base64",
            filters: [
                {
                    memcmp: positionDataMemcmp,
                },
                {
                    memcmp: {
                        offset: 8,
                        bytes: owner?.toBase58() ?? this.wallet.publicKey.toBase58(),
                    },
                },
            ],
        });
        return res.map((account) => account.pubkey);
    }
    async getStakeAccountPositions(stakeAccountPositions) {
        const account = await this.stakingProgram.provider.connection.getAccountInfo(stakeAccountPositions);
        if (account === null) {
            throw new Error("Stake account not found");
        }
        return (0, position_1.deserializeStakeAccountPositions)(stakeAccountPositions, account.data, this.stakingProgram.idl);
    }
    async getDelegationRecord(stakeAccountPositions, publisher) {
        return this.integrityPoolProgram.account.delegationRecord
            .fetchNullable((0, pdas_1.getDelegationRecordAddress)(stakeAccountPositions, publisher))
            .then((record) => (0, bn_1.convertBNToBigInt)(record));
    }
    async getStakeAccountCustody(stakeAccountPositions) {
        return (0, spl_token_1.getAccount)(this.connection, (0, pdas_1.getStakeAccountCustodyAddress)(stakeAccountPositions));
    }
    async initializePool({ rewardProgramAuthority, poolData, y, }) {
        const yAnchor = (0, bn_1.convertBigIntToBN)(y);
        const instruction = await this.integrityPoolProgram.methods
            .initializePool(rewardProgramAuthority, yAnchor)
            .accounts({
            poolData,
            slashCustody: (0, pdas_1.getStakeAccountCustodyAddress)(poolData),
        })
            .instruction();
        return (0, transaction_1.sendTransaction)([instruction], this.connection, this.wallet);
    }
    async getOwnerPythAtaAccount() {
        const globalConfig = await this.getGlobalConfig();
        return (0, spl_token_1.getAccount)(this.connection, (0, spl_token_1.getAssociatedTokenAddressSync)(globalConfig.pythTokenMint, this.wallet.publicKey, true));
    }
    async getOwnerPythBalance() {
        try {
            const ataAccount = await this.getOwnerPythAtaAccount();
            return ataAccount.amount;
        }
        catch {
            return 0n;
        }
    }
    async getPoolConfigAccount() {
        const poolConfigAnchor = await this.integrityPoolProgram.account.poolConfig.fetch((0, pdas_1.getPoolConfigAddress)());
        return (0, bn_1.convertBNToBigInt)(poolConfigAnchor);
    }
    async getPoolDataAccount() {
        const poolConfig = await this.getPoolConfigAccount();
        const poolDataAddress = poolConfig.poolData;
        const poolDataAccountAnchor = await this.integrityPoolProgram.account.poolData.fetch(poolDataAddress);
        return (0, bn_1.convertBNToBigInt)(poolDataAccountAnchor);
    }
    async stakeToGovernance(stakeAccountPositions, amount) {
        const globalConfig = await this.getGlobalConfig();
        const instructions = [];
        if (!(await this.hasGovernanceRecord(globalConfig))) {
            await (0, spl_governance_1.withCreateTokenOwnerRecord)(instructions, constants_1.GOVERNANCE_ADDRESS, spl_governance_1.PROGRAM_VERSION_V2, globalConfig.pythGovernanceRealm, this.wallet.publicKey, globalConfig.pythTokenMint, this.wallet.publicKey);
        }
        if (!(await this.hasJoinedDaoLlc(stakeAccountPositions))) {
            instructions.push(await this.getJoinDaoLlcInstruction(stakeAccountPositions));
        }
        instructions.push(await this.stakingProgram.methods
            .createPosition({
            voting: {},
        }, new anchor_1.BN(amount.toString()))
            .accounts({
            stakeAccountPositions,
        })
            .instruction(), await this.stakingProgram.methods
            .mergeTargetPositions({ voting: {} })
            .accounts({
            stakeAccountPositions,
        })
            .instruction());
        return (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
    }
    async unstakeFromGovernance(stakeAccountPositions, positionState, amount) {
        const stakeAccountPositionsData = await this.getStakeAccountPositions(stakeAccountPositions);
        const currentEpoch = await (0, clock_1.getCurrentEpoch)(this.connection);
        let remainingAmount = amount;
        const instructionPromises = [];
        const eligiblePositions = stakeAccountPositionsData.data.positions
            .map((p, i) => ({ position: p, index: i }))
            .reverse()
            .filter(({ position }) => position.targetWithParameters.voting !== undefined &&
            positionState === (0, position_1.getPositionState)(position, currentEpoch));
        for (const { position, index } of eligiblePositions) {
            if (position.amount < remainingAmount) {
                instructionPromises.push(this.stakingProgram.methods
                    .closePosition(index, (0, bn_1.convertBigIntToBN)(position.amount), {
                    voting: {},
                })
                    .accounts({
                    stakeAccountPositions,
                })
                    .instruction());
                remainingAmount -= position.amount;
            }
            else {
                instructionPromises.push(this.stakingProgram.methods
                    .closePosition(index, (0, bn_1.convertBigIntToBN)(remainingAmount), {
                    voting: {},
                })
                    .accounts({
                    stakeAccountPositions,
                })
                    .instruction());
                break;
            }
        }
        const instructions = await Promise.all(instructionPromises);
        return (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
    }
    async unstakeFromPublisher(stakeAccountPositions, publisher, positionState, amount) {
        const stakeAccountPositionsData = await this.getStakeAccountPositions(stakeAccountPositions);
        const currentEpoch = await (0, clock_1.getCurrentEpoch)(this.connection);
        let remainingAmount = amount;
        const instructionPromises = [];
        const eligiblePositions = stakeAccountPositionsData.data.positions
            .map((p, i) => ({ position: p, index: i }))
            .reverse()
            .filter(({ position }) => position.targetWithParameters.integrityPool?.publisher !==
            undefined &&
            position.targetWithParameters.integrityPool.publisher.equals(publisher) &&
            positionState === (0, position_1.getPositionState)(position, currentEpoch));
        for (const { position, index } of eligiblePositions) {
            if (position.amount < remainingAmount) {
                instructionPromises.push(this.integrityPoolProgram.methods
                    .undelegate(index, (0, bn_1.convertBigIntToBN)(position.amount))
                    .accounts({
                    stakeAccountPositions,
                    publisher,
                })
                    .instruction());
                remainingAmount -= position.amount;
            }
            else {
                instructionPromises.push(this.integrityPoolProgram.methods
                    .undelegate(index, (0, bn_1.convertBigIntToBN)(remainingAmount))
                    .accounts({
                    stakeAccountPositions,
                    publisher,
                })
                    .instruction());
                break;
            }
        }
        const instructions = await Promise.all(instructionPromises);
        return (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
    }
    async unstakeFromAllPublishers(stakeAccountPositions, positionStates) {
        const [stakeAccountPositionsData, currentEpoch] = await Promise.all([
            this.getStakeAccountPositions(stakeAccountPositions),
            (0, clock_1.getCurrentEpoch)(this.connection),
        ]);
        const instructions = await Promise.all(stakeAccountPositionsData.data.positions
            .map((position, index) => {
            const publisher = position.targetWithParameters.integrityPool?.publisher;
            return publisher === undefined
                ? undefined
                : { position, index, publisher };
        })
            // By separating this filter from the next, typescript can narrow the
            // type and automatically infer that there will be no `undefined` values
            // in the array after this line.  If we combine those filters,
            // typescript won't narrow properly.
            .filter((positionInfo) => positionInfo !== undefined)
            .filter(({ position }) => positionStates.includes((0, position_1.getPositionState)(position, currentEpoch)))
            .reverse()
            .map(({ position, index, publisher }) => this.integrityPoolProgram.methods
            .undelegate(index, (0, bn_1.convertBigIntToBN)(position.amount))
            .accounts({ stakeAccountPositions, publisher })
            .instruction()));
        return (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
    }
    async hasGovernanceRecord(config) {
        const tokenOwnerRecordAddress = await (0, spl_governance_1.getTokenOwnerRecordAddress)(constants_1.GOVERNANCE_ADDRESS, config.pythGovernanceRealm, config.pythTokenMint, this.wallet.publicKey);
        const voterAccountInfo = await this.stakingProgram.provider.connection.getAccountInfo(tokenOwnerRecordAddress);
        return Boolean(voterAccountInfo);
    }
    async createStakeAccountAndDeposit(amount) {
        const globalConfig = await this.getGlobalConfig();
        const senderTokenAccount = (0, spl_token_1.getAssociatedTokenAddressSync)(globalConfig.pythTokenMint, this.wallet.publicKey, true);
        const nonce = crypto.randomBytes(16).toString("hex");
        const stakeAccountPositions = await web3_js_1.PublicKey.createWithSeed(this.wallet.publicKey, nonce, this.stakingProgram.programId);
        const minimumBalance = await this.stakingProgram.provider.connection.getMinimumBalanceForRentExemption(constants_1.POSITIONS_ACCOUNT_SIZE);
        const instructions = [];
        instructions.push(web3_js_1.SystemProgram.createAccountWithSeed({
            fromPubkey: this.wallet.publicKey,
            newAccountPubkey: stakeAccountPositions,
            basePubkey: this.wallet.publicKey,
            seed: nonce,
            lamports: minimumBalance,
            space: constants_1.POSITIONS_ACCOUNT_SIZE,
            programId: this.stakingProgram.programId,
        }), await this.stakingProgram.methods
            .createStakeAccount(this.wallet.publicKey, { fullyVested: {} })
            .accounts({
            stakeAccountPositions,
        })
            .instruction(), await this.stakingProgram.methods
            .createVoterRecord()
            .accounts({
            stakeAccountPositions,
        })
            .instruction());
        if (!(await this.hasGovernanceRecord(globalConfig))) {
            await (0, spl_governance_1.withCreateTokenOwnerRecord)(instructions, constants_1.GOVERNANCE_ADDRESS, spl_governance_1.PROGRAM_VERSION_V2, globalConfig.pythGovernanceRealm, this.wallet.publicKey, globalConfig.pythTokenMint, this.wallet.publicKey);
        }
        instructions.push(await this.stakingProgram.methods
            .joinDaoLlc(globalConfig.agreementHash)
            .accounts({
            stakeAccountPositions,
        })
            .instruction(), (0, spl_token_1.createTransferInstruction)(senderTokenAccount, (0, pdas_1.getStakeAccountCustodyAddress)(stakeAccountPositions), this.wallet.publicKey, amount));
        await (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
        return stakeAccountPositions;
    }
    async depositTokensToStakeAccountCustody(stakeAccountPositions, amount) {
        const globalConfig = await this.getGlobalConfig();
        const mint = globalConfig.pythTokenMint;
        const senderTokenAccount = (0, spl_token_1.getAssociatedTokenAddressSync)(mint, this.wallet.publicKey, true);
        const instruction = (0, spl_token_1.createTransferInstruction)(senderTokenAccount, (0, pdas_1.getStakeAccountCustodyAddress)(stakeAccountPositions), this.wallet.publicKey, amount);
        return (0, transaction_1.sendTransaction)([instruction], this.connection, this.wallet);
    }
    async withdrawTokensFromStakeAccountCustody(stakeAccountPositions, amount) {
        const globalConfig = await this.getGlobalConfig();
        const mint = globalConfig.pythTokenMint;
        const instructions = [];
        const receiverTokenAccount = (0, spl_token_1.getAssociatedTokenAddressSync)(mint, this.wallet.publicKey, true);
        // Edge case: if the user doesn't have an ATA, create one
        try {
            await this.getOwnerPythAtaAccount();
        }
        catch {
            instructions.push((0, spl_token_1.createAssociatedTokenAccountInstruction)(this.wallet.publicKey, receiverTokenAccount, this.wallet.publicKey, mint));
        }
        instructions.push(await this.stakingProgram.methods
            .withdrawStake(new anchor_1.BN(amount.toString()))
            .accounts({
            destination: receiverTokenAccount,
            stakeAccountPositions,
        })
            .instruction());
        return (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
    }
    async stakeToPublisher(stakeAccountPositions, publisher, amount) {
        const instructions = [];
        if (!(await this.hasJoinedDaoLlc(stakeAccountPositions))) {
            instructions.push(await this.getJoinDaoLlcInstruction(stakeAccountPositions));
        }
        instructions.push(await this.integrityPoolProgram.methods
            .delegate((0, bn_1.convertBigIntToBN)(amount))
            .accounts({
            owner: this.wallet.publicKey,
            publisher,
            stakeAccountPositions,
        })
            .instruction());
        return (0, transaction_1.sendTransaction)(instructions, this.connection, this.wallet);
    }
    async getUnlockSchedule(stakeAccountPositions, includePastPeriods = false) {
        const stakeAccountMetadataAddress = (0, pdas_1.getStakeAccountMetadataAddress)(stakeAccountPositions);
        const stakeAccountMetadata = await this.stakingProgram.account.stakeAccountMetadataV2.fetch(stakeAccountMetadataAddress);
        const vestingSchedule = (0, bn_1.convertBNToBigInt)(stakeAccountMetadata.lock);
        const config = await this.getGlobalConfig();
        if (config.pythTokenListTime === null) {
            throw new Error("Pyth token list time not set in global config");
        }
        return (0, vesting_1.getUnlockSchedule)({
            vestingSchedule,
            pythTokenListTime: config.pythTokenListTime,
            includePastPeriods,
        });
    }
    async getCirculatingSupply() {
        const vestingSchedule = {
            periodicVestingAfterListing: {
                initialBalance: 8500000000n * constants_1.FRACTION_PRECISION_N,
                numPeriods: 4n,
                periodDuration: constants_1.ONE_YEAR_IN_SECONDS,
            },
        };
        const config = await this.getGlobalConfig();
        if (config.pythTokenListTime === null) {
            throw new Error("Pyth token list time not set in global config");
        }
        const unlockSchedule = (0, vesting_1.getUnlockSchedule)({
            vestingSchedule,
            pythTokenListTime: config.pythTokenListTime,
            includePastPeriods: false,
        });
        const totalLocked = unlockSchedule.schedule.reduce((total, unlock) => total + unlock.amount, 0n);
        const mint = await this.getPythTokenMint();
        return mint.supply - totalLocked;
    }
    async getAdvanceDelegationRecordInstructions(stakeAccountPositions) {
        const poolData = await this.getPoolDataAccount();
        const stakeAccountPositionsData = await this.getStakeAccountPositions(stakeAccountPositions);
        const allPublishers = (0, pool_1.extractPublisherData)(poolData);
        const publishers = allPublishers
            .map((publisher) => {
            const positionsWithPublisher = stakeAccountPositionsData.data.positions.filter(({ targetWithParameters }) => targetWithParameters.integrityPool?.publisher.equals(publisher.pubkey));
            let lowestEpoch;
            for (const position of positionsWithPublisher) {
                lowestEpoch = (0, bigint_1.bigintMin)(position.activationEpoch, lowestEpoch);
            }
            return {
                ...publisher,
                lowestEpoch,
            };
        })
            .filter(({ lowestEpoch }) => lowestEpoch !== undefined);
        const delegationRecords = await Promise.all(publishers.map(({ pubkey }) => this.getDelegationRecord(stakeAccountPositions, pubkey)));
        let lowestEpoch;
        for (const [index, publisher] of publishers.entries()) {
            const maximum = (0, bigint_1.bigintMax)(publisher.lowestEpoch, delegationRecords[index]?.lastEpoch);
            lowestEpoch = (0, bigint_1.bigintMin)(lowestEpoch, maximum);
        }
        const currentEpoch = await (0, clock_1.getCurrentEpoch)(this.connection);
        // Filter out delegationRecord that are up to date
        const filteredPublishers = publishers.filter((_, index) => {
            return !(delegationRecords[index]?.lastEpoch === currentEpoch);
        });
        // anchor does not calculate the correct pda for other programs
        // therefore we need to manually calculate the pdas
        const advanceDelegationRecordInstructions = await Promise.all(filteredPublishers.map(({ pubkey, stakeAccount }) => this.integrityPoolProgram.methods
            .advanceDelegationRecord()
            .accountsPartial({
            payer: this.wallet.publicKey,
            publisher: pubkey,
            publisherStakeAccountPositions: stakeAccount,
            publisherStakeAccountCustody: stakeAccount
                ? (0, pdas_1.getStakeAccountCustodyAddress)(stakeAccount)
                : null,
            stakeAccountPositions,
            stakeAccountCustody: (0, pdas_1.getStakeAccountCustodyAddress)(stakeAccountPositions),
        })
            .instruction()));
        const mergePositionsInstruction = await Promise.all(publishers.map(({ pubkey }) => this.integrityPoolProgram.methods
            .mergeDelegationPositions()
            .accounts({
            owner: this.wallet.publicKey,
            publisher: pubkey,
            stakeAccountPositions,
        })
            .instruction()));
        return {
            advanceDelegationRecordInstructions,
            mergePositionsInstruction,
            lowestEpoch,
        };
    }
    async advanceDelegationRecord(stakeAccountPositions) {
        const instructions = await this.getAdvanceDelegationRecordInstructions(stakeAccountPositions);
        return (0, transaction_1.sendTransaction)([
            ...instructions.advanceDelegationRecordInstructions,
            ...instructions.mergePositionsInstruction,
        ], this.connection, this.wallet);
    }
    async getClaimableRewards(stakeAccountPositions) {
        const instructions = await this.getAdvanceDelegationRecordInstructions(stakeAccountPositions);
        let totalRewards = 0n;
        for (const instruction of instructions.advanceDelegationRecordInstructions) {
            const tx = new web3_js_1.Transaction().add(instruction);
            tx.feePayer = this.wallet.publicKey;
            const res = await this.connection.simulateTransaction(tx);
            const val = res.value.returnData?.data[0];
            if (val === undefined) {
                continue;
            }
            const buffer = Buffer.from(val, "base64").reverse();
            totalRewards += BigInt("0x" + buffer.toString("hex"));
        }
        return {
            totalRewards,
            expiry: instructions.lowestEpoch === undefined
                ? undefined
                : (0, clock_1.epochToDate)(instructions.lowestEpoch + 53n),
        };
    }
    async setPublisherStakeAccount(publisher, stakeAccountPositions, newStakeAccountPositions) {
        const instruction = await this.integrityPoolProgram.methods
            .setPublisherStakeAccount()
            .accounts({
            currentStakeAccountPositionsOption: stakeAccountPositions,
            newStakeAccountPositionsOption: newStakeAccountPositions ?? null,
            publisher,
        })
            .instruction();
        await (0, transaction_1.sendTransaction)([instruction], this.connection, this.wallet);
        return;
    }
    async reassignPublisherStakeAccount(publisher, stakeAccountPositions, newStakeAccountPositions) {
        return this.setPublisherStakeAccount(publisher, stakeAccountPositions, newStakeAccountPositions);
    }
    async removePublisherStakeAccount(publisher, stakeAccountPositions) {
        return this.setPublisherStakeAccount(publisher, stakeAccountPositions, undefined);
    }
    async getTargetAccount() {
        const targetAccount = await this.stakingProgram.account.targetMetadata.fetch((0, pdas_1.getTargetAccountAddress)());
        return (0, bn_1.convertBNToBigInt)(targetAccount);
    }
    /**
     * This returns the current scaling factor between staked tokens and realms voter weight.
     * The formula is n_staked_tokens = scaling_factor * n_voter_weight
     */
    async getScalingFactor() {
        const targetAccount = await this.getTargetAccount();
        return Number(targetAccount.locked) / Number(constants_1.MAX_VOTER_WEIGHT);
    }
    async getRecoverAccountInstruction(stakeAccountPositions, governanceAuthority) {
        return this.stakingProgram.methods
            .recoverAccount()
            .accountsPartial({
            stakeAccountPositions,
            governanceAuthority,
        })
            .instruction();
    }
    async getTransferAccountInstruction(stakeAccountPositions, governanceAuthority, newOwner) {
        return this.stakingProgram.methods
            .transferAccount()
            .accountsPartial({
            stakeAccountPositions,
            governanceAuthority,
            newOwner,
        })
            .instruction();
    }
    async getUpdatePoolAuthorityInstruction(governanceAuthority, poolAuthority) {
        return this.stakingProgram.methods
            .updatePoolAuthority(poolAuthority)
            .accounts({
            governanceAuthority,
        })
            .instruction();
    }
    async getUpdateVoterWeightInstruction(stakeAccountPositions, action, remainingAccount) {
        return this.stakingProgram.methods
            .updateVoterWeight(action)
            .accounts({
            stakeAccountPositions,
        })
            .remainingAccounts(remainingAccount
            ? [
                {
                    pubkey: remainingAccount,
                    isWritable: false,
                    isSigner: false,
                },
            ]
            : [])
            .instruction();
    }
    async hasJoinedDaoLlc(stakeAccountPositions) {
        const config = await this.getGlobalConfig();
        const stakeAccountMetadataAddress = (0, pdas_1.getStakeAccountMetadataAddress)(stakeAccountPositions);
        const stakeAccountMetadata = await this.stakingProgram.account.stakeAccountMetadataV2.fetch(stakeAccountMetadataAddress);
        return (JSON.stringify(stakeAccountMetadata.signedAgreementHash) ===
            JSON.stringify(config.agreementHash));
    }
    async getJoinDaoLlcInstruction(stakeAccountPositions) {
        const config = await this.getGlobalConfig();
        return this.stakingProgram.methods
            .joinDaoLlc(config.agreementHash)
            .accounts({
            stakeAccountPositions,
        })
            .instruction();
    }
    async getMainStakeAccount(owner) {
        const stakeAccountPositions = await this.getAllStakeAccountPositions(owner);
        const currentEpoch = await (0, clock_1.getCurrentEpoch)(this.connection);
        const stakeAccountVotingTokens = await Promise.all(stakeAccountPositions.map(async (position) => {
            const stakeAccountPositionsData = await this.getStakeAccountPositions(position);
            return {
                stakeAccountPosition: position,
                votingTokens: (0, position_1.getVotingTokenAmount)(stakeAccountPositionsData, currentEpoch),
            };
        }));
        let mainAccount = stakeAccountVotingTokens[0];
        if (mainAccount === undefined) {
            return;
        }
        for (let i = 1; i < stakeAccountVotingTokens.length; i++) {
            const currentAccount = stakeAccountVotingTokens[i];
            if (currentAccount !== undefined &&
                currentAccount.votingTokens > mainAccount.votingTokens) {
                mainAccount = currentAccount;
            }
        }
        return mainAccount;
    }
    async getVoterWeight(owner) {
        const mainAccount = await this.getMainStakeAccount(owner);
        if (mainAccount === undefined) {
            return 0;
        }
        const targetAccount = await this.getTargetAccount();
        return (mainAccount.votingTokens * constants_1.MAX_VOTER_WEIGHT) / targetAccount.locked;
    }
    async getPythTokenMint() {
        const globalConfig = await this.getGlobalConfig();
        return (0, spl_token_1.getMint)(this.connection, globalConfig.pythTokenMint);
    }
    async getRewardCustodyAccount() {
        const poolConfigAddress = (0, pdas_1.getPoolConfigAddress)();
        const config = await this.getGlobalConfig();
        const rewardCustodyAccountAddress = (0, spl_token_1.getAssociatedTokenAddressSync)(config.pythTokenMint, poolConfigAddress, true);
        return (0, spl_token_1.getAccount)(this.connection, rewardCustodyAccountAddress);
    }
}
exports.PythStakingClient = PythStakingClient;
